<template>
    <div v-if="property">
        <h4 class="title is-5">
            Key Dates
        </h4>

        <b-notification
            v-if="hasInspectionDate && $dayjs().isBefore(parsedInspectionDate)"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Next inspection</strong> due on {{ parsedInspectionDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasEPCDate && isEPCPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Energy Performance Certificate</strong> (EPC) expires on {{ parsedEPCExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasPATDate && isPATPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Portable Appliance Testing</strong> certificate expires on {{ parsedPATExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasGasDate && isGasPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Gas Safety</strong> certificate expires on {{ parsedGasExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasElectricDate && isElectricPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Electrical Safety</strong> certificate expires on {{ parsedElectricExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasHMODate && isHMOPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>HMO license</strong> expires on {{ parsedHMOExpiryDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasLegionellaDate && isLegionellaPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Legionella</strong> check due on {{ parsedLegionellaDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasLicenceDate && isLicencePreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Licence</strong> date expires on {{ parsedLicenceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasDetectorDate && isDetectorPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Detector</strong> date expires on {{ parsedDetectorDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasBoilerServiceDate && isBoilerServicePreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Boiler service</strong> is due on {{ parsedBoilerServiceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasOilServiceDate && isOilServicePreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Oil service</strong> is due on {{ parsedOilServiceDate | formatDate }}.
        </b-notification>

        <b-notification
            v-if="hasCappedGasDate && isCappedGasPreExpiring"
            type="is-warning is-light"
            :closable="false"
        >
            <strong>Capped gas</strong> expires on {{ parsedCappedGasDate | formatDate }}.
        </b-notification>



        <WarningNotification v-if="hasInspectionDate && ! isInspectionPreExpiring">
            <template #header>
                Property inspection expire{{ $dayjs().isAfter(parsedInspectionDate) ? 'd' : 's' }} on {{ parsedInspectionDate | formatDate }}
            </template>
            <template #main>
                <p>
                    Property inspections ensure your property is being kept to a good standard,
                    allowing maintenance issues to be identified in their early stages.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasEPCDate && ! isEPCPreExpiring">
            <template #header>
                Energy Performance Certificate (EPC) expire{{ $dayjs().isAfter(parsedEPCExpiryDate) ? 'd' : 's' }} on {{ parsedEPCExpiryDate | formatDate }}
            </template>
            <template #main>
                <p>
                    It is a legal requirement to have an EPC in order to market your property.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasPATDate && ! isPATPreExpiring">
            <template #header>
                Portable Appliance Testing certificate expire{{ $dayjs().isAfter(parsedPATExpiryDate) ? 'd' : 's' }} on {{ parsedPATExpiryDate | formatDate }}
            </template>
            <template #main>
                <p>
                    It is advisable to have portable appliances tested at your rental property to
                    ensure the electrical appliances are safe to operate.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasGasDate && ! isGasPreExpiring">
            <template #header>
                Gas Safety certificate expire{{ $dayjs().isAfter(parsedGasExpiryDate) ? 'd' : 's' }} on {{ parsedGasExpiryDate | formatDate }}
            </template>
            <template #main>
                <p>
                    It is a legal requirement to have a valid gas safety certificate.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasElectricDate && ! isElectricPreExpiring">
            <template #header>
                Electrical Safety certificate expire{{ $dayjs().isAfter(parsedElectricExpiryDate) ? 'd' : 's' }} on {{ parsedElectricExpiryDate | formatDate }}
            </template>
            <template #main>
                <p>
                    An Electrical Safety Certificate is a document that demonstrates the safety of the electrical installations, wiring, and fuses.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasHMODate && ! isHMOPreExpiring">
            <template #header>
                HMO license expire{{ $dayjs().isAfter(parsedHMOExpiryDate) ? 'd' : 's' }} on {{ parsedHMOExpiryDate | formatDate }}
            </template>
            <template #main>
                <p>
                    You must have a licence if you’re renting out a large HMO in England or Wales.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasLegionellaDate && ! isLegionellaPreExpiring">
            <template #header>
                Legionella check expire{{ $dayjs().isAfter(parsedLegionellaDate) ? 'd' : 's' }} on {{ parsedLegionellaDate | formatDate }}
            </template>
            <template #main>
                <p>
                    There is a legal duty for landlords to assess and control the risk of exposure to legionella bacteria.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasBoilerServiceDate && ! isBoilerServicePreExpiring">
            <template #header>
                Boiler service expire{{ $dayjs().isAfter(parsedBoilerServiceDate) ? 'd' : 's' }} on {{ parsedBoilerServiceDate | formatDate }}
            </template>
            <template #main>
                <p>
                    During a service the engineer will inspect and clean the main components in the boiler to ensure they are fit for purpose.
                </p>
            </template>
        </WarningNotification>

        <WarningNotification v-if="hasOilServiceDate && ! isOilServicePreExpiring">
            <template #header>
                Oil service expire{{ $dayjs().isAfter(parsedOilServiceDate) ? 'd' : 's' }} on {{ parsedOilServiceDate | formatDate }}
            </template>
            <template #main>
                <p>
                    During a service the engineer will visually inspect the boiler and oil tank to ensure they are fit for purpose.
                </p>
            </template>
        </WarningNotification>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import WarningNotification from './WarningNotification.vue';

export default {
    components: {
        WarningNotification,
    },

    computed: {
        ...mapState('portfolioProperty', [
            'property',
        ]),
        ...mapGetters('portfolioProperty', [
            // EPC
            'hasEPCDate',
            'parsedEPCExpiryDate',
            'isEPCPreExpiring',

            // PAT
            'hasPATDate',
            'parsedPATExpiryDate',
            'isPATPreExpiring',

            // Gas
            'hasGasDate',
            'parsedGasExpiryDate',
            'isGasPreExpiring',

            // Electrical Safety Certificate
            'hasElectricDate',
            'parsedElectricExpiryDate',
            'isElectricPreExpiring',

            // HMO license
            'hasHMODate',
            'parsedHMOExpiryDate',
            'isHMOPreExpiring',

            // Inspection
            'hasInspectionDate',
            'parsedInspectionDate',
            'isInspectionPreExpiring',

            // Legionella
            'hasLegionellaDate',
            'parsedLegionellaDate',
            'isLegionellaPreExpiring',

            // Licence
            'hasLicenceDate',
            'parsedLicenceDate',
            'isLicencePreExpiring',

            // Detector
            'hasDetectorDate',
            'parsedDetectorDate',
            'isDetectorPreExpiring',

            // Boiler service
            'hasBoilerServiceDate',
            'parsedBoilerServiceDate',
            'isBoilerServicePreExpiring',

            // Oil service
            'hasOilServiceDate',
            'parsedOilServiceDate',
            'isOilServicePreExpiring',

            // Capped gas
            'hasCappedGasDate',
            'parsedCappedGasDate',
            'isCappedGasPreExpiring',
        ]),
    },
};
</script>
